import React, { useState } from 'react';
import { api } from '../../api';
import toast from 'react-hot-toast';
import { X, Mail, Phone, KeyRound, Loader2, Send } from 'lucide-react';

const InviteToPortalModal = ({ person, onClose, onInvited }) => {
    const [email, setEmail] = useState(person.email || '');
    const [phone, setPhone] = useState(person.phone || '');
    const [pin, setPin] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    
    const [emailError, setEmailError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [pinError, setPinError] = useState('');

    const handlePhoneChange = (e) => {
        const filtered = e.target.value.replace(/[^0-9]/g, '');
        if (filtered.length <= 10) {
            setPhone(filtered);
            if (phoneError) setPhoneError('');
        }
    };

    const handlePinChange = (e) => {
        const filtered = e.target.value.replace(/[^0-9]/g, '');
        if (filtered.length <= 6) {
            setPin(filtered);
            if (pinError) setPinError('');
        }
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Reset errors
        setEmailError('');
        setPhoneError('');
        setPinError('');

        // Validate
        let hasError = false;

        if (!email || !validateEmail(email)) {
            setEmailError('Valid email is required');
            hasError = true;
        }

        if (!phone || phone.length !== 10) {
            setPhoneError('Phone must be exactly 10 digits');
            hasError = true;
        }

        if (!pin || pin.length < 4 || pin.length > 6) {
            setPinError('PIN must be 4-6 digits');
            hasError = true;
        }

        if (hasError) return;

        try {
            setIsLoading(true);
            await api.inviteToPortal(person.id, {
                email,
                phone,
                quick_access_code: pin
            });
            
            toast.success(`${person.first_name} has been invited to the portal!`);
            onInvited();
            onClose();
        } catch (error) {
            toast.error(error.message || 'Failed to invite to portal');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Invite to Portal</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                        <X size={20} />
                    </button>
                </div>

                <div style={{ 
                    background: '#f8fafc', 
                    borderRadius: '8px', 
                    padding: '12px', 
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.875rem'
                    }}>
                        {person.first_name?.charAt(0)}{person.last_name?.charAt(0)}
                    </div>
                    <div>
                        <div style={{ fontWeight: 600 }}>{person.first_name} {person.last_name}</div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Housekeeper</div>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '6px' }}>
                            <Mail size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                            Email Address
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                            placeholder="housekeeper@email.com"
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: `1px solid ${emailError ? '#ef4444' : '#e2e8f0'}`,
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                boxSizing: 'border-box'
                            }}
                        />
                        {emailError && <div style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px' }}>{emailError}</div>}
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '6px' }}>
                            <Phone size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                            Phone Number
                        </label>
                        <input
                            type="tel"
                            value={phone}
                            onChange={handlePhoneChange}
                            placeholder="5551234567"
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: `1px solid ${phoneError ? '#ef4444' : '#e2e8f0'}`,
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                boxSizing: 'border-box'
                            }}
                        />
                        {phoneError && <div style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px' }}>{phoneError}</div>}
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '6px' }}>
                            <KeyRound size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                            Portal PIN
                        </label>
                        <input
                            type="password"
                            value={pin}
                            onChange={handlePinChange}
                            placeholder="4-6 digits"
                            maxLength={6}
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: `1px solid ${pinError ? '#ef4444' : '#e2e8f0'}`,
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                boxSizing: 'border-box'
                            }}
                        />
                        {pinError && <div style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px' }}>{pinError}</div>}
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                flex: 1,
                                padding: '10px 16px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                background: 'white',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: 500
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading}
                            style={{
                                flex: 1,
                                padding: '10px 16px',
                                border: 'none',
                                borderRadius: '8px',
                                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                color: 'white',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                opacity: isLoading ? 0.7 : 1
                            }}
                        >
                            {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                            {isLoading ? 'Sending...' : 'Send Invitation'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default InviteToPortalModal;
